import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { JOURNEY_NOTE_MAX, JOURNEY_PROMPT_DAYS } from '@goaltree/backend/convex/lib/journeys'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@goaltree/ui/components/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import type { TFunction } from 'i18next'
import { Camera, Check, Copy, Images, Lock, Plus, Share2, Trash2, X } from 'lucide-react'
import { type ComponentProps, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { downscale } from '@/components/dot-photo'
import { openShareCard } from '@/lib/share-card'

type Journey = NonNullable<FunctionReturnType<typeof api.journeys.forGoal>>
type Entry = Journey['entries'][number]
type Kind = Entry['kind']

export type CaptureTarget = {
  goalId: Id<'nodes'>
  kind: Kind
  milestoneId?: Id<'nodes'> | null
  milestoneTitle?: string | null
  done?: boolean
  entry?: Entry
}

const DAY_MS = 86_400_000

function captureTitle(t: TFunction, c: CaptureTarget) {
  if (c.kind === 'before') return t('journey.capture.titleBefore')
  if (c.kind === 'milestone') return c.milestoneTitle ?? t('journey.capture.titleNow')
  return t(c.done ? 'journey.capture.titleFinish' : 'journey.capture.titleNow')
}

export function JourneyCaptureDialog({
  target,
  onClose,
}: {
  target: CaptureTarget | null
  onClose: () => void
}) {
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      {target && (
        <CaptureForm key={target.entry?.id ?? target.kind} target={target} onClose={onClose} />
      )}
    </Dialog>
  )
}

function CaptureForm({ target, onClose }: { target: CaptureTarget; onClose: () => void }) {
  const { t } = useTranslation()
  const uploadUrl = useMutation(api.journeys.uploadUrl)
  const save = useMutation(api.journeys.saveEntry)
  const remove = useMutation(api.journeys.removeEntry)
  const [note, setNote] = useState(target.entry?.note ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [cleared, setCleared] = useState(false)
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file])
  const shown = preview ?? (cleared ? null : (target.entry?.photoUrl ?? null))

  async function submit() {
    setBusy(true)
    try {
      let photoId: Id<'_storage'> | undefined
      if (file) {
        const blob = await downscale(file).catch(() => file)
        const res = await fetch(await uploadUrl({}), {
          method: 'POST',
          headers: { 'Content-Type': blob.type || 'image/jpeg' },
          body: blob,
        })
        if (!res.ok) throw new Error('upload')
        photoId = ((await res.json()) as { storageId: Id<'_storage'> }).storageId
      }
      const out = await save({
        goalId: target.goalId,
        kind: target.kind,
        ...(target.milestoneId && { milestoneId: target.milestoneId }),
        note,
        ...(photoId && { photoId }),
        ...(cleared && !photoId && { clearPhoto: true }),
      })
      if (!out.ok) return void toast.error(t(`journey.capture.errors.${out.code}`))
      toast.success(t('journey.capture.saved'), {
        action: {
          label: t('journey.share'),
          onClick: () => openShareCard({ type: 'journey', ref: out.journeyId }),
        },
      })
      onClose()
    } catch {
      toast.error(t('journey.capture.errors.failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{captureTitle(t, target)}</DialogTitle>
        <DialogDescription className="flex items-center gap-1.5">
          <Lock className="size-3" />
          {t('journey.capture.private')}
        </DialogDescription>
      </DialogHeader>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) setFile(f)
          e.target.value = ''
        }}
      />
      {shown ? (
        <div className="relative mx-auto aspect-[4/5] w-full max-w-60 overflow-hidden rounded-2xl bg-muted">
          <img src={shown} alt="" className="size-full object-cover" />
          <div className="absolute inset-x-2 bottom-2 flex justify-center gap-2">
            <button
              type="button"
              onClick={() => input.current?.click()}
              className="cursor-pointer rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur hover:bg-black/70"
            >
              {t('journey.capture.changePhoto')}
            </button>
            <button
              type="button"
              aria-label={t('journey.capture.removePhoto')}
              onClick={() => {
                setFile(null)
                setCleared(true)
              }}
              className="cursor-pointer rounded-full bg-black/60 p-1.5 text-white backdrop-blur hover:bg-black/70"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="flex h-32 w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
        >
          <Camera className="size-5" />
          {t('journey.capture.addPhoto')}
        </button>
      )}
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={JOURNEY_NOTE_MAX}
        rows={3}
        placeholder={t(
          target.kind === 'before'
            ? 'journey.capture.notePlaceholderBefore'
            : 'journey.capture.notePlaceholder',
        )}
        className="w-full resize-none rounded-xl border border-border bg-transparent px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      />
      <div className="flex items-center gap-2">
        {target.entry ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              void remove({ entryId: target.entry!.id }).catch(() =>
                toast.error(t('journey.capture.errors.failed')),
              )
              onClose()
            }}
            className="flex cursor-pointer items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-destructive hover:bg-muted"
          >
            <Trash2 className="size-4" />
            {t('journey.capture.delete')}
          </button>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded-xl px-3 py-2 text-sm font-semibold text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {t('journey.capture.skip')}
          </button>
        )}
        <button
          type="button"
          disabled={busy || (!note.trim() && !shown)}
          onClick={() => void submit()}
          className="ml-auto cursor-pointer rounded-xl bg-primary px-5 py-2 text-sm font-bold text-primary-foreground hover:bg-primary/90 disabled:cursor-default disabled:opacity-50"
        >
          {t('journey.capture.save')}
        </button>
      </div>
    </DialogContent>
  )
}

function promptCopy(t: TFunction, p: { kind: Kind; milestoneTitle: string | null }, goal: string) {
  if (p.kind === 'before')
    return [t('journey.prompt.before'), t('journey.prompt.beforeBody', { goal })]
  if (p.kind === 'milestone')
    return [
      t('journey.prompt.milestone', { title: p.milestoneTitle ?? '' }),
      t('journey.prompt.milestoneBody'),
    ]
  return [t('journey.prompt.after', { goal }), t('journey.prompt.afterBody')]
}

function PromptRow({
  title,
  body,
  onCapture,
  onLater,
  className,
}: {
  title: string
  body: string
  onCapture: () => void
  onLater: () => void
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-3.5',
        className,
      )}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary-text">
        <Camera className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold">{title}</p>
        <p className="text-xs text-muted-foreground">{body}</p>
      </div>
      <button
        type="button"
        onClick={onCapture}
        className="shrink-0 cursor-pointer rounded-full bg-primary px-3.5 py-1.5 text-xs font-bold text-primary-foreground hover:bg-primary/90"
      >
        {t('journey.prompt.add')}
      </button>
      <button
        type="button"
        aria-label={t('journey.prompt.later')}
        title={t('journey.prompt.later')}
        onClick={onLater}
        className="shrink-0 cursor-pointer rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}

/** Today: the newest open capture prompt across goals, one at a time. */
export function JourneyPromptCard({ className }: { className?: string }) {
  const { t } = useTranslation()
  const since = useMemo(
    () => Math.floor(Date.now() / DAY_MS) * DAY_MS - JOURNEY_PROMPT_DAYS * DAY_MS,
    [],
  )
  const prompt = useQuery(api.journeys.nextPrompt, { since })
  const dismiss = useMutation(api.journeys.dismissPrompt)
  const [target, setTarget] = useState<CaptureTarget | null>(null)
  if (!prompt && !target) return null
  return (
    <>
      {prompt && (
        <PromptRow
          className={className}
          {...(([title, body]) => ({ title, body }))(promptCopy(t, prompt, prompt.goalTitle))}
          onCapture={() =>
            setTarget({
              goalId: prompt.goalId,
              kind: prompt.kind,
              milestoneId: prompt.milestoneId,
              milestoneTitle: prompt.milestoneTitle,
              done: prompt.kind === 'after',
            })
          }
          onLater={() => void dismiss({ promptId: prompt.id })}
        />
      )}
      <JourneyCaptureDialog target={target} onClose={() => setTarget(null)} />
    </>
  )
}

function Tile({ label, entry, onClick }: { label: string; entry: Entry; onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex aspect-[4/5] w-28 shrink-0 cursor-pointer flex-col overflow-hidden rounded-xl bg-muted text-left transition-opacity hover:opacity-90"
    >
      {entry.photoUrl ? (
        <img src={entry.photoUrl} alt="" className="absolute inset-0 size-full object-cover" />
      ) : (
        <p className="line-clamp-5 px-2.5 pt-8 text-xs font-semibold">“{entry.note}”</p>
      )}
      <span
        className={cn(
          'absolute top-1.5 left-1.5 max-w-[calc(100%-12px)] truncate rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
          entry.photoUrl ? 'bg-black/60 text-white' : 'bg-background/70 text-foreground',
        )}
      >
        {label}
      </span>
      {entry.photoUrl && entry.photoStatus !== 'ok' && (
        <span className="absolute inset-x-1.5 bottom-1.5 flex items-center justify-center gap-1 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          {entry.photoStatus === 'rejected' && <Lock className="size-2.5" />}
          {t(entry.photoStatus === 'rejected' ? 'journey.photo.rejected' : 'journey.photo.pending')}
        </span>
      )}
    </button>
  )
}

function Toggle({
  checked,
  onChange,
  label,
  hint,
  kind = 'switch',
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  hint: string
  kind?: 'switch' | 'checkbox'
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-3">
      <span className="flex flex-col">
        <span className="text-sm font-semibold">{label}</span>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </span>
      <input
        type="checkbox"
        role={kind === 'switch' ? 'switch' : undefined}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      {kind === 'switch' ? (
        <span
          aria-hidden
          className="relative mt-0.5 h-6 w-10 shrink-0 rounded-full bg-muted transition-colors peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring/50 after:absolute after:top-[3px] after:left-[3px] after:size-[18px] after:rounded-full after:bg-background after:transition-transform peer-checked:after:translate-x-4"
        />
      ) : (
        <span
          aria-hidden
          className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border border-border text-primary-foreground peer-checked:border-primary peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring/50"
        >
          {checked && <Check className="size-3.5" />}
        </span>
      )}
    </label>
  )
}

function Sharing({
  goalId,
  journey,
}: {
  goalId: Id<'nodes'>
  journey: NonNullable<Journey['journey']>
}) {
  const { t } = useTranslation()
  const setSharing = useMutation(api.journeys.setSharing)
  const update = (isPublic: boolean, featureConsent: boolean) =>
    void setSharing({ goalId, isPublic, featureConsent }).catch(() =>
      toast.error(t('journey.capture.errors.failed')),
    )
  const status =
    journey.featureConsent && journey.featureStatus
      ? journey.featureStatus === 'approved'
        ? t(journey.rewarded ? 'journey.sharing.approvedReward' : 'journey.sharing.approved')
        : journey.featureStatus === 'pending'
          ? t('journey.sharing.pending')
          : t('journey.sharing.rejected')
      : null
  return (
    <div className="flex flex-col gap-3 border-t border-border/60 pt-3">
      <Toggle
        checked={journey.isPublic}
        onChange={(on) => update(on, false)}
        label={t('journey.sharing.public')}
        hint={t('journey.sharing.publicBody')}
      />
      {journey.publicUrl && (
        <button
          type="button"
          onClick={() =>
            void navigator.clipboard
              .writeText(journey.publicUrl!)
              .then(() => toast.success(t('journey.sharing.copied')))
          }
          className="flex cursor-pointer items-center gap-1.5 self-start rounded-full border border-border px-3 py-1 text-xs font-semibold hover:bg-muted"
        >
          <Copy className="size-3.5" />
          {t('journey.sharing.copyLink')}
        </button>
      )}
      {journey.isPublic && (
        <Toggle
          kind="checkbox"
          checked={journey.featureConsent}
          onChange={(on) => update(true, on)}
          label={t('journey.sharing.feature')}
          hint={t('journey.sharing.featureBody')}
        />
      )}
      {status && <p className="text-xs font-semibold text-primary-text">{status}</p>}
      <p className="text-[11px] text-muted-foreground">{t('journey.sharing.disclaimer')}</p>
    </div>
  )
}

/** Goal screen: the before/after, its stats and check-in photos, sharing and any open prompt. */
export function JourneyPanel({ goalId, className }: { goalId: Id<'nodes'>; className?: string }) {
  const { t } = useTranslation()
  const data = useQuery(api.journeys.forGoal, { goalId })
  const dismiss = useMutation(api.journeys.dismissPrompt)
  const [target, setTarget] = useState<CaptureTarget | null>(null)
  if (!data) return null
  const { entries, journey, prompt, stats, done } = data
  const before = entries.find((e) => e.kind === 'before')
  const after = entries.find((e) => e.kind === 'after')
  const open = data.milestones.filter((m) => m.done && !entries.some((e) => e.milestoneId === m.id))
  const capture = (c: Omit<CaptureTarget, 'goalId' | 'done'>) => setTarget({ goalId, done, ...c })
  const days = Math.max(1, Math.floor(((data.endedAt ?? Date.now()) - data.startedAt) / DAY_MS) + 1)
  const labelOf = (e: Entry) =>
    e.kind === 'before'
      ? t('journey.start')
      : e.kind === 'milestone'
        ? (e.milestoneTitle ?? '')
        : done
          ? t('journey.finish')
          : t('journey.dayN', {
              n: Math.max(1, Math.floor((e.createdAt - data.startedAt) / DAY_MS) + 1),
            })
  const promptText = prompt && promptCopy(t, prompt, data.goalTitle)

  return (
    <section className={cn('flex flex-col gap-3 rounded-2xl border bg-card p-4', className)}>
      <div className="flex items-center gap-2">
        <Images className="size-4 text-primary-text" />
        <h3 className="text-sm font-bold">{t('journey.title')}</h3>
        {journey && entries.length > 0 && (
          <button
            type="button"
            onClick={() => openShareCard({ type: 'journey', ref: journey.id })}
            className="ml-auto flex cursor-pointer items-center gap-1.5 rounded-full bg-foreground px-3 py-1 text-xs font-bold text-background hover:opacity-90"
          >
            <Share2 className="size-3.5" />
            {t('journey.share')}
          </button>
        )}
      </div>

      {prompt && promptText && (
        <PromptRow
          title={promptText[0]}
          body={promptText[1]}
          onCapture={() =>
            capture({
              kind: prompt.kind,
              milestoneId: prompt.milestoneId,
              milestoneTitle: prompt.milestoneTitle,
              entry:
                prompt.kind === 'after' ? after : prompt.kind === 'before' ? before : undefined,
            })
          }
          onLater={() => void dismiss({ promptId: prompt.id })}
        />
      )}

      {entries.length === 0 && !prompt && (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold">{t('journey.intro')}</p>
          <p className="text-xs text-muted-foreground">{t('journey.introBody')}</p>
        </div>
      )}

      {entries.length > 0 && (
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          {entries.map((e) => (
            <Tile
              key={e.id}
              entry={e}
              label={labelOf(e)}
              onClick={() =>
                capture({
                  kind: e.kind,
                  milestoneId: e.milestoneId,
                  milestoneTitle: e.milestoneTitle,
                  entry: e,
                })
              }
            />
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!before && (
          <AddButton label={t('journey.addStart')} onClick={() => capture({ kind: 'before' })} />
        )}
        {open.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger className={ADD_BUTTON}>
              <Plus className="size-3.5" />
              {t('journey.addMilestone')}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {open.map((m) => (
                <DropdownMenuItem
                  key={m.id}
                  onClick={() =>
                    capture({ kind: 'milestone', milestoneId: m.id, milestoneTitle: m.title })
                  }
                >
                  {m.title}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {!after && (
          <AddButton
            label={t(done ? 'journey.addFinish' : 'journey.addNow')}
            onClick={() => capture({ kind: 'after' })}
          />
        )}
      </div>

      {entries.length > 0 && (
        <div className="grid grid-cols-4 gap-2 text-center">
          {(
            [
              [days, 'journey.statDays'],
              [stats.streak, 'journey.statStreak'],
              [stats.checkIns, 'journey.statCheckIns'],
              [stats.tasksDone, 'journey.statTasks'],
            ] as const
          ).map(([n, key]) => (
            <div key={key} className="flex flex-col rounded-xl bg-muted/50 py-2">
              <span className="font-mono text-base font-bold tabular-nums">{n}</span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t(key)}
              </span>
            </div>
          ))}
        </div>
      )}

      {entries.length > 0 && data.photos.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t('journey.alongTheWay')}
          </span>
          <div className="flex gap-1.5 overflow-x-auto">
            {data.photos.map((p) => (
              <img
                key={p.date + p.url}
                src={p.url}
                alt=""
                className="size-12 shrink-0 rounded-lg object-cover"
              />
            ))}
          </div>
        </div>
      )}

      {journey && entries.length > 0 && <Sharing goalId={goalId} journey={journey} />}

      <JourneyCaptureDialog target={target} onClose={() => setTarget(null)} />
    </section>
  )
}

const ADD_BUTTON =
  'flex cursor-pointer items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-muted'

function AddButton({ label, ...props }: { label: string } & ComponentProps<'button'>) {
  return (
    <button type="button" {...props} className={ADD_BUTTON}>
      <Plus className="size-3.5" />
      {label}
    </button>
  )
}
